import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CheckAvailabilityViewModel } from '../models/CheckAvailabilityViewModel';
import type { ExtraServicePrebookRequestViewModel } from '../models/ExtraServicePrebookRequestViewModel';
import type { ExtraServicePrebookViewModel } from '../models/ExtraServicePrebookViewModel';
import type { ExtraServiceSearchViewModel } from '../models/ExtraServiceSearchViewModel';
import type { HotelDetailReviewViewModel } from '../models/HotelDetailReviewViewModel';
import type { HotelDetailViewModel } from '../models/HotelDetailViewModel';
import type { HotelListViewModel } from '../models/HotelListViewModel';
import type { HotelPriceViewModel } from '../models/HotelPriceViewModel';
import type { HotelRecommendationsRequestViewModel } from '../models/HotelRecommendationsRequestViewModel';
import type { HotelSearchViewModel } from '../models/HotelSearchViewModel';
import type { HotelSorting } from '../models/HotelSorting';
import type { ProviderType } from '../models/ProviderType';
import type { RecommendedHotelsViewModel } from '../models/RecommendedHotelsViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class HotelsService {
  /**
   * List
   * Gets all hotels by slug
   * @returns HotelListViewModel OK
   * @throws ApiError
   */
  public static async listHotels(
    {
      keywords,
      slug,
      _in,
      out,
      sort,
      region,
      popularity,
      theme,
      feature,
      boardType,
      chain,
      star,
      category,
      point,
      refundable,
      status,
      priceMin,
      priceMax,
      room,
      page,
      size,
      basketId,
    }: {
      keywords?: string;
      slug?: string;
      _in?: string;
      out?: string;
      /**
       * Sort: popularity, priceasc or pricedesc, Default: popularity
       */
      sort?: HotelSorting;
      region?: Array<number>;
      popularity?: Array<number>;
      theme?: Array<number>;
      feature?: Array<number>;
      boardType?: Array<number>;
      chain?: Array<number>;
      star?: Array<number>;
      category?: Array<number>;
      point?: number;
      refundable?: boolean;
      status?: boolean;
      priceMin?: number;
      priceMax?: number;
      room?: Array<string>;
      page?: number;
      size?: number;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelListViewModel>({
      method: 'GET',
      url: `/hotels`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(keywords ? { keywords: keywords } : {}),
        ...(slug ? { slug: slug } : {}),
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(region ? { region: region } : {}),
        ...(popularity ? { popularity: popularity } : {}),
        ...(theme ? { theme: theme } : {}),
        ...(feature ? { feature: feature } : {}),
        ...(boardType ? { boardType: boardType } : {}),
        ...(chain ? { chain: chain } : {}),
        ...(star ? { star: star } : {}),
        ...(category ? { category: category } : {}),
        ...(point ? { point: point } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(status ? { status: status } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(room ? { room: room } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Detail
   * Gets a specific hotel by slug
   * @returns HotelDetailViewModel OK
   * @throws ApiError
   */
  public static async getHotel(
    {
      slug,
      room,
      _in,
      out,
      basketId,
    }: {
      /**
       * Hotel Slug
       */
      slug: string;
      /**
       * Room Information
       */
      room: Array<string>;
      /**
       * Checkin date
       */
      _in?: string;
      /**
       * Checkout date
       */
      out?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelDetailViewModel>({
      method: 'GET',
      url: `/hotels/${slug}`,
      path: {
        ...(slug ? { slug: slug } : {}),
      },
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(room ? { room: room } : {}),
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * @deprecated
   * Search
   * Searches hotels or locations
   * @returns HotelSearchViewModel OK
   * @throws ApiError
   */
  public static async searchHotel(
    {
      s,
      domestic,
    }: {
      /**
       * Search terms
       */
      s: string;
      /**
       * Domestic
       */
      domestic?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<HotelSearchViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<HotelSearchViewModel>>({
      method: 'GET',
      url: `/hotels/search`,
      query: {
        ...(s ? { s: s } : {}),
        ...(domestic ? { domestic: domestic } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Prices
   * Gets a specific hotel season prices
   * @returns HotelPriceViewModel OK
   * @throws ApiError
   */
  public static async getHotelPrices(
    {
      id,
    }: {
      /**
       * Hotel Id
       */
      id: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelPriceViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelPriceViewModel>({
      method: 'GET',
      url: `/hotels/${id}/prices`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Prebook (Extra Service)
   * Add prebook for extra services
   * @returns ExtraServicePrebookViewModel OK
   * @throws ApiError
   */
  public static async prebookAddExtraService(
    {
      requestBody,
    }: {
      requestBody?: ExtraServicePrebookRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ExtraServicePrebookViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ExtraServicePrebookViewModel>({
      method: 'POST',
      url: `/hotels/prebook/extraservice`,
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Search (Extra service)
   * Search for extra services
   * @returns ExtraServiceSearchViewModel OK
   * @throws ApiError
   */
  public static async searchExtraService(
    {
      guest,
      _in,
      out,
      hotelId,
      roomId,
      currency,
      hotelPrice,
      refundable,
      provider,
    }: {
      guest: string;
      _in?: string;
      out?: string;
      hotelId?: number;
      roomId?: number;
      currency?: string;
      hotelPrice?: number;
      refundable?: boolean;
      provider?: ProviderType;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ExtraServiceSearchViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ExtraServiceSearchViewModel>({
      method: 'GET',
      url: `/hotels/search/extraservice`,
      query: {
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(hotelId ? { hotelId: hotelId } : {}),
        ...(roomId ? { roomId: roomId } : {}),
        ...(currency ? { currency: currency } : {}),
        ...(hotelPrice ? { hotelPrice: hotelPrice } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(provider ? { provider: provider } : {}),
        ...(guest ? { guest: guest } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Recommendations (Hotel)
   * Gets recommendations for Hotels
   * @returns RecommendedHotelsViewModel OK
   * @throws ApiError
   */
  public static async hotelRecommendations(
    {
      requestBody,
    }: {
      requestBody?: HotelRecommendationsRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RecommendedHotelsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<RecommendedHotelsViewModel>({
      method: 'POST',
      url: `/hotels/recommendations`,
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Availability (Hotel)
   * Gets availability status
   * @returns CheckAvailabilityViewModel OK
   * @throws ApiError
   */
  public static async getAvailability(
    {
      _in,
      out,
      roomId,
    }: {
      _in?: string;
      out?: string;
      roomId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CheckAvailabilityViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CheckAvailabilityViewModel>({
      method: 'GET',
      url: `/hotels/availability`,
      query: {
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(roomId ? { roomId: roomId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Review Detail
   * Gets reviews for hotel
   * @returns HotelDetailReviewViewModel OK
   * @throws ApiError
   */
  public static async getReviews(
    {
      id,
    }: {
      id: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelDetailReviewViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelDetailReviewViewModel>({
      method: 'GET',
      url: `/hotels/reviews/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
